import { useMemo, useState, useRef, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus, Loader2, Users, Calendar, Repeat } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useAppointmentsInRange,
  useRescheduleAppointment,
  useUpdateAppointmentDuration,
  useAppointmentsRealtime,
  appointmentsRangeQueryKey,
  type AppointmentListItem,
  type ListFilters,
} from '@/hooks/useAppointments';
import {
  useGroupSessionsInRange,
  useGroupSessionsRealtime,
  type GroupSessionListItem,
} from '@/hooks/useGroupSessions';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinic } from '@/hooks/useClinicSettings';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { AppointmentModal } from './AppointmentModal';
import { AppointmentDetailsModal } from './AppointmentDetailsModal';
import { GroupSessionModal } from './GroupSessionModal';
import { MonthView } from './MonthView';
import { AgendaView } from './AgendaView';
import { RescheduleConfirmationModal } from '@/components/appointments/RescheduleConfirmationModal';

type View = 'day' | 'week' | 'month' | 'agenda';
type LayoutView = 'time' | 'resource';

const HOUR_HEIGHT = 56; // px per hour
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
const SLOT_MINUTES = 30;
const HOURS = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i);

interface DragData {
  kind: 'reschedule' | 'resize-end';
  appointment: AppointmentListItem;
}

interface DropData {
  date: string;
  resourceId?: string; // clinician_id when dropping in resource view
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
function startOfWeekMonday(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const dow = date.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + offset);
  return date;
}

function startOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(23, 59, 59, 999);
  return date;
}

function addDays(d: Date, n: number): Date {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function snapToFifteenMin(deltaY: number): number {
  const pxPer15Min = HOUR_HEIGHT / 4;
  return Math.round(deltaY / pxPer15Min) * 15;
}

function extractRpcErrorMessage(err: unknown): string | null {
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }
  return null;
}

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

// Danish weekday keys for business_hours lookup
const BUSINESS_HOURS_KEYS = ['sondag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lordag'] as const;

interface DayHours {
  enabled: boolean;
  start: string;
  end: string;
}

function parseBusinessHours(businessHoursJson: unknown): Map<number, DayHours | null> {
  const map = new Map<number, DayHours | null>();
  if (!businessHoursJson || typeof businessHoursJson !== 'object' || Array.isArray(businessHoursJson)) {
    return map;
  }
  const obj = businessHoursJson as Record<string, unknown>;
  BUSINESS_HOURS_KEYS.forEach((key, dayOfWeek) => {
    const val = obj[key];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const day = val as Record<string, unknown>;
      const enabled = Boolean(day.enabled);
      const start = typeof day.start === 'string' ? day.start : '08:00';
      const end = typeof day.end === 'string' ? day.end : '16:00';
      map.set(dayOfWeek, { enabled, start, end });
    }
  });
  return map;
}

// ---------------------------------------------------------------------------
// Appointments page
// ---------------------------------------------------------------------------
export function Appointments() {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const qc = useQueryClient();

  const { data: clinic } = useClinic(activeClinicId);

  const [view, setView] = useState<View>(() => {
    const stored = localStorage.getItem('appointments-view');
    return stored && ['day', 'week', 'month', 'agenda'].includes(stored)
      ? (stored as View)
      : 'week';
  });
  const [layoutView, setLayoutView] = useState<LayoutView>('time');
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  const [clinicianFilter, setClinicianFilter] = useState<string | null>(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[] | null>(null);

  // Active drag (reschedule or resize) — used to render DragOverlay
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
  // Id of the appointment that caused a conflict — shows red ring briefly
  const [conflictAptId, setConflictAptId] = useState<string | null>(null);
  // Pending reschedule awaiting confirmation
  const [pendingReschedule, setPendingReschedule] = useState<{
    appointment: AppointmentListItem;
    newStartsAt: Date;
    newEndsAt: Date;
    newClinicianId: string | null;
    oldStartsAt: Date;
    oldEndsAt: Date;
  } | null>(null);

  // Persist view selection to localStorage
  useEffect(() => {
    localStorage.setItem('appointments-view', view);
  }, [view]);

  const days = useMemo(() => {
    // Resource view always shows a single day
    if (layoutView === 'resource') return [anchor];
    if (view === 'day') return [anchor];
    if (view === 'month' || view === 'agenda') {
      // For month/agenda, fetch the entire month
      const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      return [monthStart, monthEnd];
    }
    const start = startOfWeekMonday(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [view, anchor, layoutView]);

  const range = useMemo<ListFilters>(
    () => ({
      start: days[0]?.toISOString() ?? new Date().toISOString(),
      end: endOfDay(days[days.length - 1] ?? new Date()).toISOString(),
      // In resource view, don't filter by clinician since we show all resources
      clinicianId: layoutView === 'resource' ? null : clinicianFilter,
    }),
    [days, clinicianFilter, layoutView],
  );

  const { data: appointments, isLoading, error } = useAppointmentsInRange(activeClinicId, range);
  const { data: groupSessions } = useGroupSessionsInRange(
    activeClinicId,
    range,
  );

  const { data: members } = useClinicMembers(activeClinicId);

  // Parse business hours for visual greying of non-business hours
  const businessHours = useMemo(() => {
    if (!clinic?.business_hours) return new Map<number, DayHours | null>();
    return parseBusinessHours(clinic.business_hours);
  }, [clinic?.business_hours]);

  // Resources for resource view: filtered list of staff members
  const resources = useMemo(() => {
    if (layoutView !== 'resource' || !members) return [];
    const staffList = selectedStaffIds === null 
      ? members 
      : members.filter((m) => selectedStaffIds.includes(m.user_id));
    return staffList.map((m) => ({
      id: m.user_id,
      name: m.name,
      email: m.email,
      role: m.role,
    }));
  }, [layoutView, members, selectedStaffIds]);

  // Realtime live-update subscriptions
  useAppointmentsRealtime(activeClinicId, range);
  useGroupSessionsRealtime(activeClinicId, range);

  const reschedule = useRescheduleAppointment();
  const updateDuration = useUpdateAppointmentDuration();

  const [modalState, setModalState] = useState<
    | { type: 'appointment'; mode: 'create'; initialStart: Date | null; initialEnd: Date | null }
    | { type: 'appointment'; mode: 'edit'; appointmentId: string }
    | { type: 'appointment'; mode: 'details'; appointmentId: string }
    | { type: 'group_session'; mode: 'create'; initialStart: Date | null }
    | { type: 'group_session'; mode: 'edit'; sessionId: string }
    | null
  >(null);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [i18n.language],
  );

  const monthLabel = useMemo(() => {
    if (view === 'day') return dateFormatter.format(anchor);
    if (view === 'month') {
      return new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
        month: 'long',
        year: 'numeric',
      }).format(anchor);
    }
    if (view === 'agenda') {
      const start = days[0];
      const end = days[days.length - 1];
      if (!start || !end) return '';
      return `${start.getDate()}. – ${dateFormatter.format(end)}`;
    }
    const start = days[0];
    const end = days[days.length - 1];
    if (!start || !end) return '';
    return `${start.getDate()}. – ${dateFormatter.format(end)}`;
  }, [view, anchor, dateFormatter, days, i18n.language]);

  // DnD sensors: require 8px movement before activating (preserves click handlers)
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 8 } }));

  // Stable reference to the current range query key for optimistic cache updates
  const rangeQueryKey = useMemo(
    () => appointmentsRangeQueryKey(activeClinicId ?? null, range),
    [activeClinicId, range],
  );

  function handleDragStart(event: DragStartEvent) {
    const data = event.active.data.current as DragData | undefined;
    if (data) setActiveDrag(data);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over, delta } = event;
    setActiveDrag(null);

    if (!over || !active.data.current) return;

    const dragData = active.data.current as DragData;
    const { kind, appointment } = dragData;
    const overData = over.data.current as DropData | undefined;
    if (!overData?.date) return;

    const targetDay = startOfDay(new Date(overData.date));
    const origStart = new Date(appointment.starts_at);
    const origEnd = new Date(appointment.ends_at);
    const durationMs = origEnd.getTime() - origStart.getTime();
    const origStartMinutes = origStart.getHours() * 60 + origStart.getMinutes();
    const deltaMinutes = snapToFifteenMin(delta.y);

    if (kind === 'reschedule') {
      const rawMinutes = origStartMinutes + deltaMinutes;
      // Clamp so appointment stays within the visible window
      const clampedMinutes = Math.max(
        DAY_START_HOUR * 60,
        Math.min((DAY_END_HOUR - 1) * 60 - 14, rawMinutes),
      );
      const newStartsAt = new Date(targetDay);
      newStartsAt.setHours(Math.floor(clampedMinutes / 60), clampedMinutes % 60, 0, 0);
      const newEndsAt = new Date(newStartsAt.getTime() + durationMs);

      // Check if clinician changed (resource view)
      const newClinicianId = overData.resourceId ?? appointment.clinician_id;

      // Skip if nothing changed
      if (
        newStartsAt.toISOString() === origStart.toISOString() &&
        isSameDay(targetDay, origStart) &&
        newClinicianId === appointment.clinician_id
      )
        return;

      // Store for confirmation instead of executing immediately
      setPendingReschedule({
        appointment,
        newStartsAt,
        newEndsAt,
        newClinicianId,
        oldStartsAt: origStart,
        oldEndsAt: origEnd,
      });
    }

    if (kind === 'resize-end') {
      const origEndMinutes = origEnd.getHours() * 60 + origEnd.getMinutes();
      const rawEndMinutes = origEndMinutes + deltaMinutes;
      // Minimum 15 min duration; cap at end of visible day
      const clampedEndMinutes = Math.max(
        origStartMinutes + 15,
        Math.min(DAY_END_HOUR * 60, rawEndMinutes),
      );
      // Resize stays on the same day as the original appointment
      const newEndsAt = new Date(startOfDay(origStart));
      newEndsAt.setHours(Math.floor(clampedEndMinutes / 60), clampedEndMinutes % 60, 0, 0);

      if (newEndsAt.toISOString() === origEnd.toISOString()) return;

      const prevData = qc.getQueryData<AppointmentListItem[]>(rangeQueryKey);
      qc.setQueryData<AppointmentListItem[]>(rangeQueryKey, (old) =>
        (old ?? []).map((a) =>
          a.id === appointment.id ? { ...a, ends_at: newEndsAt.toISOString() } : a,
        ),
      );

      updateDuration.mutate(
        {
          appointment_id: appointment.id,
          new_ends_at: newEndsAt.toISOString(),
        },
        {
          onError: (err) => {
            qc.setQueryData(rangeQueryKey, prevData);
            const msg = extractRpcErrorMessage(err) ?? '';
            if (msg.includes('conflict')) {
              setConflictAptId(appointment.id);
              setTimeout(() => setConflictAptId(null), 2000);
              toast.error(t('appointments.conflict_error'));
            } else {
              toast.error(t('appointments.errors.unknown'));
            }
          },
          onSuccess: () => toast.success(t('appointments.duration_updated')),
        },
      );
    }
  }

  function handleDragCancel() {
    setActiveDrag(null);
  }

  function executeReschedule() {
    if (!pendingReschedule) return;

    const { appointment, newStartsAt, newEndsAt, newClinicianId } = pendingReschedule;

    const prevData = qc.getQueryData<AppointmentListItem[]>(rangeQueryKey);
    qc.setQueryData<AppointmentListItem[]>(rangeQueryKey, (old) =>
      (old ?? []).map((a) =>
        a.id === appointment.id
          ? {
              ...a,
              starts_at: newStartsAt.toISOString(),
              ends_at: newEndsAt.toISOString(),
              clinician_id: newClinicianId,
            }
          : a,
      ),
    );

    reschedule.mutate(
      {
        appointment_id: appointment.id,
        starts_at: newStartsAt.toISOString(),
        ends_at: newEndsAt.toISOString(),
        clinician_id: newClinicianId,
      },
      {
        onError: (err) => {
          qc.setQueryData(rangeQueryKey, prevData);
          const msg = extractRpcErrorMessage(err) ?? '';
          if (msg.includes('conflict')) {
            setConflictAptId(appointment.id);
            setTimeout(() => setConflictAptId(null), 2000);
            toast.error(t('appointments.conflict_error'));
          } else {
            toast.error(t('appointments.errors.unknown'));
          }
        },
        onSuccess: () => toast.success(t('appointments.reschedule_success')),
      },
    );

    setPendingReschedule(null);
  }

  if (!activeClinicId) {
    return (
      <div className="p-6">
        <ErrorAlert message={t('appointments.no_clinic')} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-page-light dark:bg-page-dark">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-card-dark">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('appointments.title')}
            </h1>
            <span className="text-sm text-gray-500 dark:text-gray-400">{monthLabel}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {layoutView === 'time' && (
              <select
                value={clinicianFilter ?? ''}
                onChange={(e) => setClinicianFilter(e.target.value || null)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('appointments.select_clinician')}</option>
                {members?.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name}
                  </option>
                ))}
              </select>
            )}

            {layoutView === 'resource' && (
              <select
                multiple
                value={selectedStaffIds ?? []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
                  setSelectedStaffIds(selected.length === 0 ? null : selected);
                }}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                size={3}
              >
                {members?.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name}
                  </option>
                ))}
              </select>
            )}

            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600">
              <button
                type="button"
                onClick={() => setLayoutView('time')}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 ${
                  layoutView === 'time'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                }`}
                title={t('appointments.time_view')}
              >
                <Calendar className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setLayoutView('resource')}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 ${
                  layoutView === 'resource'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                }`}
                title={t('appointments.resource_view')}
              >
                <Users className="h-4 w-4" />
              </button>
            </div>

            {layoutView === 'time' && (
              <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => setView('day')}
                  className={`px-3 py-1.5 text-sm ${
                    view === 'day'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('appointments.view_day')}
                </button>
                <button
                  type="button"
                  onClick={() => setView('week')}
                  className={`px-3 py-1.5 text-sm ${
                    view === 'week'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('appointments.view_week')}
                </button>
                <button
                  type="button"
                  onClick={() => setView('month')}
                  className={`px-3 py-1.5 text-sm ${
                    view === 'month'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('appointments.view_month')}
                </button>
                <button
                  type="button"
                  onClick={() => setView('agenda')}
                  className={`px-3 py-1.5 text-sm ${
                    view === 'agenda'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('appointments.view_agenda')}
                </button>
              </div>
            )}

            <div className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  if (view === 'day') {
                    setAnchor((a) => addDays(a, -1));
                  } else if (view === 'month' || view === 'agenda') {
                    setAnchor((a) => {
                      const newDate = new Date(a);
                      newDate.setMonth(newDate.getMonth() - 1);
                      return newDate;
                    });
                  } else {
                    setAnchor((a) => addDays(a, -7));
                  }
                }}
                className="rounded-md border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                aria-label="Previous"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setAnchor(startOfDay(new Date()))}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              >
                {t('appointments.today')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (view === 'day') {
                    setAnchor((a) => addDays(a, 1));
                  } else if (view === 'month' || view === 'agenda') {
                    setAnchor((a) => {
                      const newDate = new Date(a);
                      newDate.setMonth(newDate.getMonth() + 1);
                      return newDate;
                    });
                  } else {
                    setAnchor((a) => addDays(a, 7));
                  }
                }}
                className="rounded-md border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                aria-label="Next"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <Button
              onClick={() =>
                setModalState({
                  type: 'appointment',
                  mode: 'create',
                  initialStart: null,
                  initialEnd: null,
                })
              }
            >
              <Plus className="mr-1 h-4 w-4" />
              {t('appointments.new_appointment')}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                setModalState({
                  type: 'group_session',
                  mode: 'create',
                  initialStart: null,
                })
              }
            >
              <Users className="mr-1 h-4 w-4" />
              {t('appointments.new_group_session')}
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-4">
        {error && <ErrorAlert message={t('appointments.load_error')} />}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : view === 'month' ? (
          <MonthView
            anchor={anchor}
            appointments={appointments ?? []}
            onDayClick={(date) => {
              setAnchor(date);
              setView('day');
            }}
          />
        ) : view === 'agenda' ? (
          <AgendaView
            appointments={appointments ?? []}
            onAppointmentClick={(id) => setModalState({ type: 'appointment', mode: 'details', appointmentId: id })}
          />
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <CalendarGrid
              days={days}
              appointments={appointments ?? []}
              groupSessions={groupSessions ?? []}
              conflictAptId={conflictAptId}
              isDnDActive={activeDrag !== null}
              layoutView={layoutView}
              resources={resources}
              businessHours={businessHours}
              onSlotClick={(start, _resourceId) =>
                setModalState({
                  type: 'appointment',
                  mode: 'create',
                  initialStart: start,
                  initialEnd: null,
                })
              }
              onSlotDragCreate={(start, end, _resourceId) =>
                setModalState({
                  type: 'appointment',
                  mode: 'create',
                  initialStart: start,
                  initialEnd: end,
                })
              }
              onAppointmentClick={(id) =>
                setModalState({ type: 'appointment', mode: 'details', appointmentId: id })
              }
              onGroupSessionClick={(id) =>
                setModalState({ type: 'group_session', mode: 'edit', sessionId: id })
              }
            />

            {/* Ghost overlay follows cursor while dragging */}
            <DragOverlay dropAnimation={null}>
              {activeDrag ? (
                <AppointmentGhost appointment={activeDrag.appointment} language={i18n.language} />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {modalState && activeClinicId && (
        <>
          {modalState.type === 'appointment' && modalState.mode === 'details' && (
            <AppointmentDetailsModal
              appointmentId={modalState.appointmentId}
              clinicId={activeClinicId}
              onClose={() => setModalState(null)}
              onEdit={() => {
                const appointmentId = modalState.appointmentId;
                setModalState({ type: 'appointment', mode: 'edit', appointmentId });
              }}
            />
          )}
          {modalState.type === 'appointment' && (modalState.mode === 'create' || modalState.mode === 'edit') && (
            <AppointmentModal
              clinicId={activeClinicId}
              mode={modalState.mode}
              appointmentId={modalState.mode === 'edit' ? modalState.appointmentId : null}
              initialStart={modalState.mode === 'create' ? modalState.initialStart : null}
              initialEnd={modalState.mode === 'create' ? modalState.initialEnd : null}
              initialClinicianId={clinicianFilter}
              onClose={() => setModalState(null)}
            />
          )}
          {modalState.type === 'group_session' && (
            <GroupSessionModal
              clinicId={activeClinicId}
              mode={modalState.mode}
              sessionId={modalState.mode === 'edit' ? modalState.sessionId : null}
              initialStart={modalState.mode === 'create' ? modalState.initialStart : null}
              onClose={() => setModalState(null)}
            />
          )}
        </>
      )}

      {/* Reschedule Confirmation Modal */}
      {pendingReschedule && (
        <RescheduleConfirmationModal
          patientName={pendingReschedule.appointment.patient_name}
          serviceName={pendingReschedule.appointment.service_name ?? undefined}
          oldDate={pendingReschedule.oldStartsAt}
          newDate={pendingReschedule.newStartsAt}
          oldTime={`${new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }).format(pendingReschedule.oldStartsAt)} - ${new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }).format(pendingReschedule.oldEndsAt)}`}
          newTime={`${new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }).format(pendingReschedule.newStartsAt)} - ${new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }).format(pendingReschedule.newEndsAt)}`}
          onConfirm={executeReschedule}
          onCancel={() => setPendingReschedule(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CalendarGrid — header row + droppable day columns with 30-min slot rows.
// Empty slot areas support quick-create by drag-selection (mousedown/mouseup).
// In resource view, columns represent staff members instead of days.
// ---------------------------------------------------------------------------
function CalendarGrid({
  days,
  appointments,
  groupSessions,
  conflictAptId,
  isDnDActive,
  layoutView,
  resources,
  businessHours,
  onSlotClick,
  onSlotDragCreate,
  onAppointmentClick,
  onGroupSessionClick,
}: {
  days: Date[];
  appointments: AppointmentListItem[];
  groupSessions: GroupSessionListItem[];
  conflictAptId: string | null;
  isDnDActive: boolean;
  layoutView: LayoutView;
  resources: Array<{ id: string; name: string; email: string }>;
  businessHours: Map<number, DayHours | null>;
  onSlotClick: (start: Date, resourceId?: string) => void;
  onSlotDragCreate: (start: Date, end: Date, resourceId?: string) => void;
  onAppointmentClick: (id: string) => void;
  onGroupSessionClick: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => startOfDay(new Date()), []);

  // Quick-create drag selection state
  const mouseDownRef = useRef(false);
  const [quickSel, setQuickSel] = useState<{
    dayIso: string;
    startMin: number;
    endMin: number;
    resourceId?: string;
  } | null>(null);

  // Listen for global mouseup to finalise quick-create even when released outside a slot
  useEffect(() => {
    function handleMouseUp() {
      if (!mouseDownRef.current) return;
      mouseDownRef.current = false;
      if (quickSel) {
        const day = new Date(quickSel.dayIso);
        const startH = Math.floor(quickSel.startMin / 60);
        const startM = quickSel.startMin % 60;
        const endH = Math.floor(quickSel.endMin / 60);
        const endM = quickSel.endMin % 60;
        const start = new Date(day);
        start.setHours(startH, startM, 0, 0);
        const end = new Date(day);
        end.setHours(endH, endM, 0, 0);
        if (quickSel.endMin > quickSel.startMin) {
          onSlotDragCreate(start, end, quickSel.resourceId);
        } else {
          onSlotClick(start, quickSel.resourceId);
        }
        setQuickSel(null);
      }
    }
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [quickSel, onSlotClick, onSlotDragCreate]);

  // Group appointments by day (for time view) or by resource (for resource view)
  const byDay = useMemo(() => {
    const map = new Map<string, AppointmentListItem[]>();
    for (const a of appointments) {
      const dt = new Date(a.starts_at);
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return map;
  }, [appointments]);

  const byResource = useMemo(() => {
    const map = new Map<string, AppointmentListItem[]>();
    for (const a of appointments) {
      const key = a.clinician_id ?? 'unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return map;
  }, [appointments]);

  const groupSessionsByDay = useMemo(() => {
    const map = new Map<string, GroupSessionListItem[]>();
    for (const gs of groupSessions) {
      const dt = new Date(gs.starts_at);
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(gs);
    }
    return map;
  }, [groupSessions]);


  function dayKey(d: Date) {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  const locale = i18n.language === 'da' ? 'da-DK' : 'en-US';

  // Compute non-business hours overlays for each day
  const nonBusinessHoursOverlays = useMemo(() => {
    const overlays: Array<{ dayIso: string; top: number; height: number }> = [];
    
    if (layoutView !== 'time') return overlays; // Only apply to time view
    
    for (const day of days) {
      const dayOfWeek = day.getDay(); // 0 = Sunday, 6 = Saturday
      const hours = businessHours.get(dayOfWeek);
      
      if (!hours || !hours.enabled) {
        // Entire day is closed
        overlays.push({
          dayIso: day.toISOString(),
          top: 0,
          height: (DAY_END_HOUR - DAY_START_HOUR) * HOUR_HEIGHT,
        });
      } else {
        // Parse business hours
        const [startHour, startMin] = hours.start.split(':').map(Number);
        const [endHour, endMin] = hours.end.split(':').map(Number);
        
        // Grey before opening
        if (startHour > DAY_START_HOUR || (startHour === DAY_START_HOUR && startMin > 0)) {
          const startMinutes = startHour * 60 + startMin - DAY_START_HOUR * 60;
          overlays.push({
            dayIso: day.toISOString(),
            top: 0,
            height: (startMinutes / 60) * HOUR_HEIGHT,
          });
        }
        
        // Grey after closing
        if (endHour < DAY_END_HOUR || (endHour === DAY_END_HOUR && endMin < 0)) {
          const endMinutes = endHour * 60 + endMin - DAY_START_HOUR * 60;
          const top = (endMinutes / 60) * HOUR_HEIGHT;
          const height = ((DAY_END_HOUR - DAY_START_HOUR) * 60 - endMinutes) / 60 * HOUR_HEIGHT;
          overlays.push({
            dayIso: day.toISOString(),
            top,
            height,
          });
        }
      }
    }
    
    return overlays;
  }, [days, businessHours, layoutView]);

  // Columns: either days (time view) or resources (resource view)
  const columns = layoutView === 'time' ? days : resources.length > 0 ? resources : [];

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
      {/* Column header row */}
      <div
        className="grid border-b border-gray-200 dark:border-gray-700"
        style={{ gridTemplateColumns: `64px repeat(${columns.length}, minmax(0, 1fr))` }}
      >
        <div className="bg-gray-50 dark:bg-gray-800" />
        {layoutView === 'time'
          ? // Time view: show day headers
            (columns as Date[]).map((d) => {
              const dow = (d.getDay() + 6) % 7;
              return (
                <div
                  key={d.toISOString()}
                  className={`border-l border-gray-200 px-2 py-2 text-center dark:border-gray-700 ${
                    isSameDay(d, today)
                      ? 'bg-indigo-50 dark:bg-indigo-900/20'
                      : 'bg-gray-50 dark:bg-gray-800'
                  }`}
                >
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t(`appointments.weekdays_short.${WEEKDAY_KEYS[dow]}`)}
                  </div>
                  <div
                    className={`text-base font-semibold ${
                      isSameDay(d, today)
                        ? 'text-indigo-600 dark:text-indigo-300'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {d.getDate()}
                  </div>
                </div>
              );
            })
          : // Resource view: show resource headers
            (columns as Array<{ id: string; name: string; email: string }>).map((res) => (
              <div
                key={res.id}
                className="border-l border-gray-200 px-3 py-2 text-center bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
              >
                <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {res.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {res.email}
                </div>
              </div>
            ))}
      </div>

      {/* Time grid */}
      <div
        className="relative grid"
        style={{ gridTemplateColumns: `64px repeat(${columns.length}, minmax(0, 1fr))` }}
      >
        {/* Hours column */}
        <div>
          {HOURS.map((h) => (
            <div
              key={h}
              className="border-b border-gray-100 pr-2 text-right text-[11px] text-gray-500 dark:border-gray-800 dark:text-gray-400"
              style={{ height: `${HOUR_HEIGHT}px` }}
            >
              <span className="relative -top-1.5">{`${String(h).padStart(2, '0')}:00`}</span>
            </div>
          ))}
        </div>

        {/* Droppable columns */}
        {layoutView === 'time'
          ? // Time view: day columns
            (columns as Date[]).map((d) => {
              const dayAppointments = byDay.get(dayKey(d)) ?? [];
              const dayOverlays = nonBusinessHoursOverlays.filter(
                (o) => o.dayIso === d.toISOString()
              );
              return (
                <DroppableColumn key={d.toISOString()} date={d} resourceId={undefined}>
                  {/* Non-business hours overlays */}
                  {dayOverlays.map((overlay, idx) => (
                    <div
                      key={`overlay-${d.toISOString()}-${idx}`}
                      className="absolute left-0 right-0 pointer-events-none bg-gray-100/60 dark:bg-gray-700/40"
                      style={{
                        top: `${overlay.top}px`,
                        height: `${overlay.height}px`,
                        zIndex: 0,
                      }}
                    />
                  ))}

                  {/* Slot rows — support quick-create by mousedown/up */}
                  {HOURS.map((h) => (
                    <div
                      key={h}
                      className="border-b border-gray-100 dark:border-gray-800"
                      style={{ height: `${HOUR_HEIGHT}px` }}
                    >
                      {[0, 1].map((half) => {
                        const slotMin =
                          DAY_START_HOUR * 60 + (h - DAY_START_HOUR) * 60 + half * SLOT_MINUTES;
                        const isSelected =
                          quickSel &&
                          quickSel.dayIso === d.toISOString() &&
                          slotMin >= quickSel.startMin &&
                          slotMin < quickSel.endMin;
                        return (
                          <div
                            key={half}
                            className={`block h-1/2 w-full select-none ${
                              isSelected
                                ? 'bg-indigo-100/70 dark:bg-indigo-900/30'
                                : 'hover:bg-indigo-50/40 dark:hover:bg-indigo-900/20'
                            }`}
                            aria-label={`${String(h).padStart(2, '0')}:${half === 0 ? '00' : '30'}`}
                            onMouseDown={(e) => {
                              if (e.button !== 0 || isDnDActive) return;
                              e.preventDefault();
                              mouseDownRef.current = true;
                              setQuickSel({
                                dayIso: d.toISOString(),
                                startMin: slotMin,
                                endMin: slotMin + SLOT_MINUTES,
                              });
                            }}
                            onMouseEnter={() => {
                              if (!mouseDownRef.current || !quickSel) return;
                              if (quickSel.dayIso !== d.toISOString()) return;
                              setQuickSel((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      endMin: Math.max(
                                        prev.startMin + SLOT_MINUTES,
                                        slotMin + SLOT_MINUTES,
                                      ),
                                    }
                                  : null,
                              );
                            }}
                          />
                        );
                      })}
                    </div>
                  ))}

                  {/* Appointment blocks */}
                  {dayAppointments.map((a) => (
                    <DraggableAppointmentBlock
                      key={a.id}
                      appointment={a}
                      onClick={() => onAppointmentClick(a.id)}
                      language={locale}
                      isConflict={conflictAptId === a.id}
                    />
                  ))}
                  
                  {/* Group Session blocks */}
                  {(groupSessionsByDay.get(dayKey(d)) ?? []).map((gs) => (
                    <GroupSessionBlock
                      key={gs.id}
                      session={gs}
                      onClick={() => onGroupSessionClick(gs.id)}
                      language={locale}
                    />
                  ))}
                </DroppableColumn>
              );
            })
          : // Resource view: resource columns (single day)
            (columns as Array<{ id: string; name: string; email: string }>).map((res) => {
              const resourceAppointments = byResource.get(res.id) ?? [];
              const dayDate = days[0];
              return (
                <DroppableColumn key={res.id} date={dayDate} resourceId={res.id}>
                  {/* Slot rows */}
                  {HOURS.map((h) => (
                    <div
                      key={h}
                      className="border-b border-gray-100 dark:border-gray-800"
                      style={{ height: `${HOUR_HEIGHT}px` }}
                    >
                      {[0, 1].map((half) => {
                        const slotMin =
                          DAY_START_HOUR * 60 + (h - DAY_START_HOUR) * 60 + half * SLOT_MINUTES;
                        const isSelected =
                          quickSel &&
                          quickSel.resourceId === res.id &&
                          slotMin >= quickSel.startMin &&
                          slotMin < quickSel.endMin;
                        return (
                          <div
                            key={half}
                            className={`block h-1/2 w-full select-none ${
                              isSelected
                                ? 'bg-indigo-100/70 dark:bg-indigo-900/30'
                                : 'hover:bg-indigo-50/40 dark:hover:bg-indigo-900/20'
                            }`}
                            aria-label={`${String(h).padStart(2, '0')}:${half === 0 ? '00' : '30'}`}
                            onMouseDown={(e) => {
                              if (e.button !== 0 || isDnDActive) return;
                              e.preventDefault();
                              mouseDownRef.current = true;
                              setQuickSel({
                                dayIso: dayDate.toISOString(),
                                startMin: slotMin,
                                endMin: slotMin + SLOT_MINUTES,
                                resourceId: res.id,
                              });
                            }}
                            onMouseEnter={() => {
                              if (!mouseDownRef.current || !quickSel) return;
                              if (quickSel.resourceId !== res.id) return;
                              setQuickSel((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      endMin: Math.max(
                                        prev.startMin + SLOT_MINUTES,
                                        slotMin + SLOT_MINUTES,
                                      ),
                                    }
                                  : null,
                              );
                            }}
                          />
                        );
                      })}
                    </div>
                  ))}

                  {/* Appointment blocks */}
                  {resourceAppointments.map((a) => (
                    <DraggableAppointmentBlock
                      key={a.id}
                      appointment={a}
                      onClick={() => onAppointmentClick(a.id)}
                      language={locale}
                      isConflict={conflictAptId === a.id}
                    />
                  ))}
                </DroppableColumn>
              );
            })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DroppableColumn — wraps a column with useDroppable so appointments can
// be dragged to a different day or resource.
// ---------------------------------------------------------------------------
function DroppableColumn({
  date,
  resourceId,
  children,
}: {
  date: Date;
  resourceId?: string;
  children: ReactNode;
}) {
  const columnId = resourceId ? `col-res-${resourceId}` : `col-${date.toISOString()}`;
  const { setNodeRef, isOver } = useDroppable({
    id: columnId,
    data: { date: date.toISOString(), resourceId } satisfies DropData,
  });
  return (
    <div
      ref={setNodeRef}
      className={`relative border-l border-gray-200 transition-colors dark:border-gray-700 ${
        isOver ? 'bg-indigo-50/40 dark:bg-indigo-900/10' : ''
      }`}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DraggableAppointmentBlock — appointment pill with drag-to-reschedule and
// bottom-edge drag-to-resize.
// ---------------------------------------------------------------------------
function DraggableAppointmentBlock({
  appointment,
  onClick,
  language,
  isConflict,
}: {
  appointment: AppointmentListItem;
  onClick: () => void;
  language: string;
  isConflict: boolean;
}) {
  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `reschedule-${appointment.id}`,
    data: { kind: 'reschedule', appointment } satisfies DragData,
  });

  const start = new Date(appointment.starts_at);
  const end = new Date(appointment.ends_at);
  const startMinutes = (start.getHours() - DAY_START_HOUR) * 60 + start.getMinutes();
  const durationMinutes = Math.max(15, (end.getTime() - start.getTime()) / 60_000);

  if (start.getHours() >= DAY_END_HOUR) return null;
  if (end.getHours() < DAY_START_HOUR) return null;

  const top = Math.max(0, (startMinutes / 60) * HOUR_HEIGHT);
  const height = Math.max(20, (durationMinutes / 60) * HOUR_HEIGHT - 2);
  const color = appointment.service_color || '#6366F1';

  const visitStatusColors: Record<string, string> = {
    checked_in:        '#3B82F6', // blue-500
    under_treatment:   '#F97316', // orange-500
    treatment_finished:'#22C55E', // green-500
    payment_completed: '#A855F7', // purple-500
  };
  const visitBorderColor = appointment.visit_status ? visitStatusColors[appointment.visit_status] : null;

  const formatter = new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    top: `${top}px`,
    height: `${height}px`,
    backgroundColor: color,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 1,
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`absolute left-1 right-1 cursor-grab overflow-hidden rounded-md text-left text-xs text-white shadow-sm transition hover:opacity-90 active:cursor-grabbing ${
        isConflict ? 'animate-pulse ring-4 ring-red-500 ring-offset-1' : 'ring-1 ring-black/10'
      }`}
      onClick={onClick}
      title={t('appointments.drag_to_reschedule')}
    >
      {/* Visit status left border stripe */}
      <div className="flex h-full">
        {visitBorderColor && (
          <div className="w-1.5 flex-shrink-0 rounded-l-md" style={{ backgroundColor: visitBorderColor }} />
        )}
        <div className="min-w-0 flex-1 px-2 py-1">
      <div className="flex items-center justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight">{appointment.patient_name}</div>
        </div>
        {(appointment.recurrence_pattern && appointment.recurrence_pattern !== 'none') && (
          <Repeat className="h-3 w-3 flex-shrink-0 opacity-70" />
        )}
      </div>
      <div className="opacity-90">
        {formatter.format(start)} – {formatter.format(end)}
      </div>
      {appointment.service_name && (
        <div className="truncate opacity-80">{appointment.service_name}</div>
      )}
      {/* Resize handle — separate draggable at the bottom edge */}
      <ResizeHandle appointment={appointment} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResizeHandle — bottom-edge drag to change duration.
// ---------------------------------------------------------------------------
function ResizeHandle({ appointment }: { appointment: AppointmentListItem }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `resize-${appointment.id}`,
    data: { kind: 'resize-end', appointment } satisfies DragData,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      className="absolute bottom-0 left-0 right-0 h-2 cursor-s-resize rounded-b bg-black/20 hover:bg-black/40"
      title={t('appointments.resize_handle')}
      style={{ touchAction: 'none' }}
    />
  );
}

// ---------------------------------------------------------------------------
// AppointmentGhost — semi-transparent overlay rendered inside DragOverlay
// while a drag is active.
// ---------------------------------------------------------------------------
function AppointmentGhost({
  appointment,
  language,
}: {
  appointment: AppointmentListItem;
  language: string;
}) {
  const color = appointment.service_color || '#6366F1';
  const start = new Date(appointment.starts_at);
  const end = new Date(appointment.ends_at);
  const durationMinutes = Math.max(15, (end.getTime() - start.getTime()) / 60_000);
  const height = Math.max(20, (durationMinutes / 60) * HOUR_HEIGHT - 2);

  const formatter = new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className="w-28 cursor-grabbing overflow-hidden rounded-md px-2 py-1 text-xs text-white shadow-lg ring-2 ring-white/40"
      style={{ backgroundColor: color, height: `${height}px`, opacity: 0.85 }}
    >
      <div className="font-semibold leading-tight">{appointment.patient_name}</div>
      <div className="opacity-90">
        {formatter.format(start)} – {formatter.format(end)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GroupSessionBlock — displays a group session on the calendar
// ---------------------------------------------------------------------------
function GroupSessionBlock({
  session,
  onClick,
  language,
}: {
  session: GroupSessionListItem;
  onClick: () => void;
  language: string;
}) {
  const { t } = useTranslation();

  const start = new Date(session.starts_at);
  const end = new Date(session.ends_at);
  const startMinutes = (start.getHours() - DAY_START_HOUR) * 60 + start.getMinutes();
  const durationMinutes = Math.max(15, (end.getTime() - start.getTime()) / 60_000);

  if (start.getHours() >= DAY_END_HOUR) return null;
  if (end.getHours() < DAY_START_HOUR) return null;

  const top = Math.max(0, (startMinutes / 60) * HOUR_HEIGHT);
  const height = Math.max(20, (durationMinutes / 60) * HOUR_HEIGHT - 2);
  const color = session.color || '#8B5CF6';

  const formatter = new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
  });

  const spotsAvailable = session.max_participants - session.current_participants;
  const isFull = spotsAvailable <= 0;

  return (
    <div
      onClick={onClick}
      className="absolute left-1 right-1 cursor-pointer overflow-hidden rounded-md px-2 py-1 text-left text-xs text-white shadow-sm ring-1 ring-black/10 transition hover:opacity-90"
      style={{
        top: `${top}px`,
        height: `${height}px`,
        backgroundColor: color,
        opacity: session.status === 'cancelled' ? 0.5 : 1,
      }}
      title={t('appointments.click_to_view')}
    >
      <div className="flex items-center gap-1">
        <Users className="h-3 w-3 flex-shrink-0" />
        <div className="flex-1 truncate font-semibold leading-tight">{session.name}</div>
      </div>
      <div className="opacity-90">
        {formatter.format(start)} – {formatter.format(end)}
      </div>
      <div className="flex items-center gap-1 text-[10px] opacity-80">
        <span>
          {session.current_participants}/{session.max_participants}
        </span>
        {isFull && <span className="font-semibold">(FULL)</span>}
      </div>
    </div>
  );
}
